import { useEffect } from "react";
import { isTelevisionRuntime } from "../utils/platformCapabilities";

type LockableScreenOrientation = ScreenOrientation & {
  lock?: (orientation: "landscape") => Promise<void>;
  unlock?: () => void;
};

function isMobileWebRuntime() {
  const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const mobileUserAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(
    navigator.userAgent,
  );
  return navigator.maxTouchPoints > 0 && (coarsePointer || mobileUserAgent);
}

/**
 * Ask phone and tablet browsers to keep Player in landscape. Browsers only
 * honor this API in supported fullscreen or installed-app contexts, so retry
 * when fullscreen changes and leave unsupported iOS Safari as a safe no-op.
 */
export function usePlayerOrientationLock() {
  useEffect(() => {
    if (isTelevisionRuntime() || !isMobileWebRuntime()) return;
    const orientation = window.screen.orientation as
      | LockableScreenOrientation
      | undefined;
    const requestLandscape = () => {
      void orientation?.lock?.("landscape").catch(() => undefined);
    };
    requestLandscape();
    document.addEventListener("fullscreenchange", requestLandscape);
    window.addEventListener("orientationchange", requestLandscape);
    return () => {
      document.removeEventListener("fullscreenchange", requestLandscape);
      window.removeEventListener("orientationchange", requestLandscape);
      orientation?.unlock?.();
    };
  }, []);
}
